import React, { useState } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { MapPin, Calendar, DollarSign, Users, CheckCircle2, Trophy } from "lucide-react";
import { Toaster, toast } from "sonner";

// Google Form configuration
const GOOGLE_FORM_ACTION = "https://docs.google.com/forms/d/e/1FAIpQLSefdn6BTZve9wqEcxFXEsWqQ0S8K15jQm4dMuKvvQMVaUocEQ/formResponse";
const FORM_FIELDS = {
  teamName: "entry.1374791767",
  teamContact: "entry.825665325",
  churchSchool: "entry.813561195",
  over18: "entry.1657638950"
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

function App() {
  const [formData, setFormData] = useState({
    team_name: "",
    contact_info: "",
    church_school: "",
    all_over_18: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRadioChange = (value) => {
    setFormData(prev => ({ ...prev, all_over_18: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.team_name || !formData.contact_info || formData.all_over_18 === null) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Create form data for Google Forms
      const googleFormData = new FormData();
      googleFormData.append(FORM_FIELDS.teamName, formData.team_name);
      googleFormData.append(FORM_FIELDS.teamContact, formData.contact_info);
      googleFormData.append(FORM_FIELDS.churchSchool, formData.church_school || "");
      googleFormData.append(FORM_FIELDS.over18, formData.all_over_18 ? "Yes" : "No");

      // Submit to Google Forms using no-cors mode (won't get response but will submit)
      await fetch(GOOGLE_FORM_ACTION, {
        method: "POST",
        body: googleFormData,
        mode: "no-cors"
      });

      toast.success("Team registered successfully!");
      setSubmitted(true);
    } catch (error) {
      // Even with no-cors, the form usually submits successfully
      toast.success("Team registered successfully!");
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      team_name: "",
      contact_info: "",
      church_school: "",
      all_over_18: null
    });
    setSubmitted(false);
  };

  return (
    <div className="app-container">
      <Toaster position="top-center" richColors />
      
      {/* Hero Section */}
      <section className="hero-section" data-testid="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          {/* Logo */}
          <motion.div 
            className="logo-container"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            <img 
              src="/hawks_logo.png" 
              alt="HCS Hawks Logo" 
              className="hawks-logo"
              data-testid="hawks-logo"
            />
          </motion.div>

          {/* Title */}
          <motion.h1 
            className="hero-title"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            data-testid="hero-title"
          >
            HCS VOLLEYBALL<br />TOURNAMENT
          </motion.h1>

          {/* Event Info Ticker */}
          <motion.div 
            className="event-ticker"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.5 }}
          >
            <div className="ticker-item">
              <Calendar size={20} />
              <span>March 21st</span>
            </div>
            <div className="ticker-divider"></div>
            <div className="ticker-item">
              <MapPin size={20} />
              <span>Hilltop Gym</span>
            </div>
            <div className="ticker-divider"></div>
            <div className="ticker-item">
              <span>6:30 PM</span>
            </div>
          </motion.div>

          {/* CTA Button */}
          <motion.a 
            href="#register"
            className="cta-button"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-testid="register-cta-button"
          >
            <Trophy size={20} />
            REGISTER YOUR TEAM
          </motion.a>
        </div>
      </section>

      {/* Info Section - Bento Grid */}
      <motion.section 
        className="info-section"
        variants={staggerContainer}
        initial="initial"
        whileInView="animate"
        viewport={{ once: true }}
      >
        <div className="bento-grid">
          <motion.div className="bento-card large" variants={fadeInUp} data-testid="info-location">
            <MapPin size={32} className="card-icon" />
            <h3>LOCATION</h3>
            <p>Hilltop Gym</p>
            <span className="card-detail">Get ready to compete!</span>
          </motion.div>
          
          <motion.div className="bento-card" variants={fadeInUp} data-testid="info-time">
            <Calendar size={28} className="card-icon" />
            <h3>DATE & TIME</h3>
            <p>March 21st @ 6:30pm</p>
          </motion.div>
          
          <motion.div className="bento-card highlight" variants={fadeInUp} data-testid="info-cost">
            <DollarSign size={28} className="card-icon" />
            <h3>ENTRY FEE</h3>
            <p className="price">$80<span>/team</span></p>
          </motion.div>
          
          <motion.div className="bento-card" variants={fadeInUp} data-testid="info-format">
            <Users size={28} className="card-icon" />
            <h3>FORMAT</h3>
            <p>Team Competition</p>
          </motion.div>
        </div>
      </motion.section>

      {/* Registration Form Section */}
      <section className="registration-section" id="register">
        <motion.div 
          className="form-container"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="form-header">
            <h2>TEAM REGISTRATION</h2>
            <p>Secure your spot in the tournament</p>
          </div>

          {submitted ? (
            <motion.div 
              className="success-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="registration-success"
            >
              <CheckCircle2 size={64} className="success-icon" />
              <h3>Registration Complete!</h3>
              <p>Thank you for registering. A confirmation has been sent to the tournament organizers.</p>
              <p className="payment-reminder">
                <strong>Next Step:</strong> Please complete your payment of $80 via Venmo to secure your spot.
              </p>
              <a 
                href="https://account.venmo.com/u/SuperRicky" 
                target="_blank" 
                rel="noopener noreferrer"
                className="venmo-button"
                data-testid="venmo-pay-button"
              >
                <DollarSign size={20} />
                Pay $80 via Venmo
              </a>
              <button 
                onClick={resetForm} 
                className="reset-button"
                data-testid="register-another-btn"
              >
                Register Another Team
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="registration-form">
              {/* Team Name */}
              <div className="form-group">
                <label htmlFor="team_name">
                  Team Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="team_name"
                  name="team_name"
                  value={formData.team_name}
                  onChange={handleInputChange}
                  placeholder="Enter your team name"
                  required
                  data-testid="input-team-name"
                />
              </div>

              {/* Contact Info (Name & Phone) */}
              <div className="form-group">
                <label htmlFor="contact_info">
                  Team Contact (Name, Phone #) <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="contact_info"
                  name="contact_info"
                  value={formData.contact_info}
                  onChange={handleInputChange}
                  placeholder="John Doe, (555) 123-4567"
                  required
                  data-testid="input-contact-info"
                />
              </div>

              {/* Church/School */}
              <div className="form-group">
                <label htmlFor="church_school">
                  Associated Church/School <span className="optional">(Optional)</span>
                </label>
                <input
                  type="text"
                  id="church_school"
                  name="church_school"
                  value={formData.church_school}
                  onChange={handleInputChange}
                  placeholder="If applicable"
                  data-testid="input-church-school"
                />
              </div>

              {/* Over 18 Radio */}
              <div className="form-group">
                <label>
                  Are all players on team over 18yo? <span className="required">*</span>
                </label>
                <div className="radio-group" data-testid="radio-over-18">
                  <label className={`radio-label ${formData.all_over_18 === true ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="all_over_18"
                      checked={formData.all_over_18 === true}
                      onChange={() => handleRadioChange(true)}
                      data-testid="radio-yes"
                    />
                    <span className="radio-custom"></span>
                    Yes
                  </label>
                  <label className={`radio-label ${formData.all_over_18 === false ? 'selected' : ''}`}>
                    <input
                      type="radio"
                      name="all_over_18"
                      checked={formData.all_over_18 === false}
                      onChange={() => handleRadioChange(false)}
                      data-testid="radio-no"
                    />
                    <span className="radio-custom"></span>
                    No
                  </label>
                </div>
              </div>

              {/* Payment Info */}
              <div className="payment-info" data-testid="payment-info">
                <DollarSign size={20} />
                <div>
                  <strong>Payment: $80 per team</strong>
                  <p>Please use Venmo to pay. Add team or contact name when submitting payment.</p>
                  <a 
                    href="https://account.venmo.com/u/SuperRicky" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="venmo-link"
                    data-testid="venmo-link"
                  >
                    Pay via Venmo @SuperRicky
                  </a>
                </div>
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                className="submit-button"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                data-testid="submit-registration-btn"
              >
                {isSubmitting ? (
                  <span className="loading">Registering...</span>
                ) : (
                  <>
                    <CheckCircle2 size={20} />
                    SUBMIT REGISTRATION
                  </>
                )}
              </motion.button>
            </form>
          )}
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <p>HCS Volleyball Tournament 2026 • Hilltop Christian School</p>
        <p className="contact">Questions? Contact us at sports@hilltopcs.org</p>
      </footer>
    </div>
  );
}

export default App;
